import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useTheme,
  Chip,
  IconButton,
} from "@mui/material";
import React, { useState, useContext, useMemo } from "react";
import { ScholarshipActionMenu } from "./KebabMenu";
import { FlexRow } from "../../components";
import ScholarshipDetailsModal from "./view-application/scholarship-details-modal";
import AuditTrailModal from "./modals/audit-trail-modal";
import api from "../../api/axiosInstance";
import { ScholarshipApplicationDetailsProps } from "../../typings/scholarship-applications";
import { SCHOLARSHIP_APPLICATION_INITIAL_VALUES } from "../../data/scholarship-applications";
import { AiOutlineEye, AiOutlineAudit } from "react-icons/ai";
import { ROWS_PER_PAGE_OPTIONS } from "../../data/pagination";
import { AuthContext } from "../../context/AuthContext";
import {
  ADMIN_PERMISSIONS,
  SUPER_ADMIN_PERMISSIONS,
} from "../../data/roles";
import { UserRolesEnum } from "../../typings";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any) => string;
}

interface ScholarshipApplicationsTableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  searchTerm?: string;
  pagination: {
    page_number: number;
    page_size: number;
    total_no_of_records: number;
  };
  onPageChange: (e: unknown, page: number) => void;
  onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRefresh: () => void;
}

export const ViewButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: "6px 10px",
  transition: "all 0.2s ease-in-out",
  backgroundColor: theme.palette.colors.green["900"],
  color: theme.palette.colors.white["500"],
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-1px)",
  },
  "&:disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

export const IconActionButton = styled(IconButton)(({ theme }) => ({
  width: "36px",
  height: "36px",
  borderRadius: "8px",
  transition: "all 0.2s ease-in-out",
  backgroundColor: theme.palette.colors.green["900"],
  color: theme.palette.colors.white["500"],
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    transform: "translateY(-1px)",
  },
  "&:disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  marginRight: "8px",
}));

// ─── Skeleton Rows ────────────────────────────────────────────────────────────
const SkeletonRow: React.FC<{ colCount: number }> = ({ colCount }) => (
  <TableRow>
    {Array.from({ length: colCount }).map((_, i) => (
      <TableCell key={i}>
        <Skeleton
          variant="rounded"
          animation="wave"
          height={18}
          sx={{
            borderRadius: "6px",
            bgcolor: "rgba(32, 99, 71, 0.07)",
            "&::after": {
              background:
                "linear-gradient(90deg, transparent, rgba(32,99,71,0.12), transparent)",
            },
          }}
          width={i === 0 ? "40%" : i === colCount - 1 ? "60%" : "75%"}
        />
      </TableCell>
    ))}
    {/* Actions column skeleton */}
    <TableCell align="center">
      <Box display="flex" justifyContent="center" gap={1}>
        <Skeleton variant="rounded" animation="wave" width={36} height={36} sx={{ borderRadius: "8px", bgcolor: "rgba(32, 99, 71, 0.07)" }} />
        <Skeleton variant="rounded" animation="wave" width={36} height={36} sx={{ borderRadius: "8px", bgcolor: "rgba(32, 99, 71, 0.07)" }} />
        <Skeleton variant="circular" animation="wave" width={32} height={32} sx={{ bgcolor: "rgba(32, 99, 71, 0.07)" }} />
      </Box>
    </TableCell>
  </TableRow>
);

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ searchTerm?: string }> = ({ searchTerm }) => {
  const hasSearch = Boolean(searchTerm?.trim());

  return (
    <TableRow>
      <TableCell colSpan={100} sx={{ border: "none", py: 0 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            px: 3,
            gap: 2,
          }}
        >
          {/* Illustrated circle icon */}
          <Box
            sx={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #ECFDF3 0%, #D1FADF 100%)",
              border: "2px solid rgba(32, 99, 71, 0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: -6,
                borderRadius: "50%",
                border: "1px dashed rgba(32, 99, 71, 0.2)",
              },
            }}
          >
            {hasSearch ? (
              /* Search icon – magnifier with no results cross */
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <circle cx="19" cy="19" r="11" stroke="#206347" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M27 27L37 37" stroke="#206347" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M16 19h6M19 16v6" stroke="#339F71" strokeWidth="2" strokeLinecap="round" />
                <line x1="15" y1="22" x2="23" y2="14" stroke="#F04438" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            ) : (
              /* Document stack icon */
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
                <rect x="10" y="14" width="24" height="22" rx="3" fill="#D1FADF" stroke="#206347" strokeWidth="1.8" />
                <rect x="13" y="10" width="20" height="20" rx="3" fill="#ECFDF3" stroke="#339F71" strokeWidth="1.5" strokeDasharray="3 2" />
                <line x1="17" y1="22" x2="27" y2="22" stroke="#206347" strokeWidth="1.8" strokeLinecap="round" />
                <line x1="17" y1="26" x2="23" y2="26" stroke="#339F71" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="32" cy="32" r="6" fill="#339F71" />
                <path d="M30 32h4M32 30v4" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            )}
          </Box>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#206347",
              fontSize: "1.1rem",
              letterSpacing: "-0.01em",
            }}
          >
            {hasSearch ? "No results found" : "No applications yet"}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#667085",
              textAlign: "center",
              maxWidth: 340,
              lineHeight: 1.6,
              fontSize: "0.875rem",
            }}
          >
            {hasSearch ? (
              <>
                We couldn't find any applications matching{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: "#344054",
                    backgroundColor: "#ECFDF3",
                    border: "1px solid #A6F4C5",
                    borderRadius: "4px",
                    px: 0.75,
                    py: 0.25,
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {searchTerm}
                </Box>
                . Try adjusting your search term or check the CNIC / name.
              </>
            ) : (
              "Scholarship applications submitted through the portal will appear here. Use the button above to get started."
            )}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
};

// ─── Main Table ───────────────────────────────────────────────────────────────
const ScholarshipApplicationsTable: React.FC<
  ScholarshipApplicationsTableProps
> = ({
  columns,
  data,
  loading = false,
  searchTerm = "",
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
}) => {
  const theme = useTheme();
  const auth = useContext(AuthContext);
  const isAdmin = [...ADMIN_PERMISSIONS, ...SUPER_ADMIN_PERMISSIONS].includes(
    auth?.role as UserRolesEnum
  );

  const [orderBy, setOrderBy] = useState<keyof any>(columns[0].id);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [application, setApplication] = useState<ScholarshipApplicationDetailsProps>(
    SCHOLARSHIP_APPLICATION_INITIAL_VALUES
  );
  const [auditTrailModalOpen, setAuditTrailModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);

  const getStatusColors = (status: string) => {
    switch (status) {
      case "Approved":
        return { color: "#10B981", bgColor: "rgba(16, 185, 129, 0.1)", borderColor: "rgba(16, 185, 129, 0.2)" };
      case "Paid":
        return { color: "#6938EF", bgColor: "rgba(105, 56, 239, 0.1)", borderColor: "rgba(105, 56, 239, 0.2)" };
      case "Submitted":
        return { color: "#3B82F6", bgColor: "rgba(59, 130, 246, 0.1)", borderColor: "rgba(59, 130, 246, 0.2)" };
      case "Rejected":
        return { color: "#EF4444", bgColor: "rgba(239, 68, 68, 0.1)", borderColor: "rgba(239, 68, 68, 0.2)" };
      case "In Process":
        return { color: "#F59E0B", bgColor: "rgba(245, 158, 11, 0.1)", borderColor: "rgba(245, 158, 11, 0.2)" };
      default:
        return { color: "#6B7280", bgColor: "rgba(107, 114, 128, 0.1)", borderColor: "rgba(107, 114, 128, 0.2)" };
    }
  };

  const renderStatusChip = (status: string) => {
    const { color, bgColor, borderColor } = getStatusColors(status);
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          backgroundColor: bgColor,
          color: color,
          border: `1px solid ${borderColor}`,
          fontWeight: 600,
          fontSize: "0.75rem",
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  };

  const handleSort =
    (property: keyof any) => (event: React.MouseEvent<unknown>) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
      if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, order, orderBy]);

  const [modalOpen, setModalOpen] = useState(false);
  const [loadingId, setLoadingId] = useState<number | null>(null);

  const handleViewDetails = async (id: number) => {
    setLoadingId(id);
    try {
      const response = await api.get(`scholarship-applications/${id}/`);
      const application = response.data.data;
      setApplication(application);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching application details:", error);
    } finally {
      setLoadingId(null);
    }
  };

  const handleViewAuditTrail = (id: number) => {
    setSelectedApplicationId(id);
    setAuditTrailModalOpen(true);
  };

  // Number of skeleton rows to show = current page_size (capped at 8 for aesthetics)
  const skeletonRowCount = Math.min(pagination.page_size, 8);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        boxShadow: 3,
        borderRadius: 2,
        transition: "opacity 0.25s ease",
        opacity: loading ? 0.85 : 1,
      }}
    >
      <TableContainer sx={{ maxHeight: 630 }}>
        <Table stickyHeader aria-label="scholarship applications table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={handleSort(column.id)}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {column.label}
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="center">
                <Typography variant="subtitle2" fontWeight="bold">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              /* ── Skeleton shimmer rows ── */
              Array.from({ length: skeletonRowCount }).map((_, i) => (
                <SkeletonRow key={i} colCount={columns.length} />
              ))
            ) : sortedData.length === 0 ? (
              /* ── Beautiful empty state ── */
              <EmptyState searchTerm={searchTerm} />
            ) : (
              /* ── Real data rows ── */
              sortedData.map((row, index) => (
                <TableRow
                  role="checkbox"
                  tabIndex={-1}
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: theme.palette.colors["white"]["400"],
                    },
                    "&:nth-of-type(even)": {
                      backgroundColor: "white",
                    },
                  }}
                >
                  {columns.map((column) => {
                    const value = row[column.id];
                    return (
                      <TableCell key={column.id} align={column.align}>
                        {column.id === "status"
                          ? renderStatusChip(value)
                          : column.format
                          ? column.format(value)
                          : value}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center">
                    <FlexRow justify="center" align="center">
                      <Tooltip title="View application details" arrow placement="top" leaveDelay={0}>
                        <span>
                          <IconActionButton
                            onClick={() => handleViewDetails(row.id)}
                            disabled={loadingId === row.id}
                          >
                            {loadingId === row.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AiOutlineEye size={18} />
                            )}
                          </IconActionButton>
                        </span>
                      </Tooltip>
                      {isAdmin && (
                        <Tooltip title="View audit trail" arrow placement="top" leaveDelay={0}>
                          <span>
                            <IconActionButton onClick={() => handleViewAuditTrail(row.id)}>
                              <AiOutlineAudit size={18} />
                            </IconActionButton>
                          </span>
                        </Tooltip>
                      )}
                      <ScholarshipActionMenu application={row} onRefresh={onRefresh} />
                    </FlexRow>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={pagination.total_no_of_records}
        rowsPerPage={pagination.page_size}
        page={pagination.page_number - 1}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />

      <ScholarshipDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        application={application}
        onRefresh={onRefresh}
      />
      {selectedApplicationId && (
        <AuditTrailModal
          open={auditTrailModalOpen}
          onClose={() => setAuditTrailModalOpen(false)}
          scholarshipApplicationId={selectedApplicationId}
        />
      )}
    </Paper>
  );
};

export default ScholarshipApplicationsTable;
